// src/pages/admin/grn/GRNPayments.jsx
import React, { useState, useEffect, useCallback } from 'react'
import {
  DollarSign,
  Search,
  Filter,
  Calendar,
  Building,
  Truck,
  Eye,
  Trash2,
  Plus,
  RefreshCw,
  Download,
  CreditCard,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  User,
  Package,
  X
} from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import InlineLoader from '../../../components/ui/InlineLoader'
import ButtonSpinner from '../../../components/ui/ButtonSpinner'
import GRNService from '../../../services/grnService'
import BranchService from '../../../services/branchService'
import SuppliersService from '../../../services/suppliersService'
import PayGRNModal from '../../../components/modals/PayGRNModal'
import DeleteConfirmModal from '../../../components/modals/DeleteConfirmModal'
import AuthService from '../../../services/authService'
import { usePermissions } from '../../../hooks/usePermissions'

// Enhanced Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-in slide-in-from-right-full duration-300 ${
    type === 'success' 
      ? 'bg-green-500 text-white' 
      : type === 'error'
      ? 'bg-red-500 text-white'
      : type === 'warning'
      ? 'bg-yellow-500 text-white'
      : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <XCircle className="h-5 w-5" /> : 
     <AlertCircle className="h-5 w-5" />}
    <span className="font-medium">{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2 hover:bg-black hover:bg-opacity-20 rounded p-1">
      <X className="h-4 w-4" />
    </button>
  </div>
)

// Enhanced Stats Card Component
const StatsCard = ({ icon: IconComponent, title, value, color = "blue" }) => (
  <Card className="hover:shadow-md transition-shadow duration-200">
    <CardContent className="p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <IconComponent className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
)

// Payment method constants
const PAYMENT_METHODS = {
  CASH: 'cash',
  CHEQUE: 'cheque',
  BANK_TRANSFER: 'bank_transfer',
  CARD: 'card'
}

const GRNPayments = () => {
  // User permissions and RBAC
  const { hasPermission } = usePermissions()
  const currentUser = AuthService.getCurrentUser()
  const userBranchId = currentUser?.branchId
  const isManager = currentUser?.role?.toLowerCase() === 'manager'
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin'
  
  console.log('🔐 GRN Payments RBAC Check:', {
    userId: currentUser?._id,
    role: currentUser?.role,
    roleLower: currentUser?.role?.toLowerCase(),
    branchId: userBranchId,
    branchName: currentUser?.branch?.name || 'Unknown',
    isManager,
    isAdmin,
    fullUser: currentUser,
    permissions: {
      canViewAllBranches: hasPermission('grn.viewAll'),
      canManagePayments: hasPermission('grn.managePayments')
    }
  })

  // State management
  const [paymentsData, setPaymentsData] = useState({ payments: [], summary: {} })
  const [filteredPayments, setFilteredPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [dataLoading, setDataLoading] = useState(false)
  const [error, setError] = useState(null)
  const [toast, setToast] = useState(null)

  // Filter states
  const [searchTerm, setSearchTerm] = useState('')
  const [branchFilter, setBranchFilter] = useState(
    isManager ? (userBranchId || '') : ''
  )
  const [supplierFilter, setSupplierFilter] = useState('')
  const [paymentDateFilter, setPaymentDateFilter] = useState('')
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('')
  const [dateRangeFilter, setDateRangeFilter] = useState({ start: '', end: '' })

  // Modal states
  const [showPayModal, setShowPayModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [selectedGRN, setSelectedGRN] = useState(null)

  // Dropdown data
  const [branches, setBranches] = useState([])
  const [suppliers, setSuppliers] = useState([])

  // Enforce branch filter for managers
  useEffect(() => {
    if (isManager && userBranchId && branchFilter !== userBranchId) {
      console.log('🔒 Enforcing branch filter for manager:', userBranchId)
      setBranchFilter(userBranchId)
    }
  }, [isManager, userBranchId, branchFilter])

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  // Enhanced refresh function
  const handleRefresh = async () => {
    console.log('🔄 Manual refresh triggered')
    setDataLoading(true)
    
    try {
      await Promise.all([
        fetchPaymentsData(),
        fetchBranches(),
        fetchSuppliers()
      ])
      showToast('Data refreshed successfully', 'success')
    } catch (error) {
      console.error('❌ Error during refresh:', error)
      showToast('Failed to refresh data', 'error')
    } finally {
      setDataLoading(false)
    }
  }

  // Fetch GRN payments data
  const fetchPaymentsData = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('💰 Fetching GRN payments data...')

      // For managers, add branch filter to API call
      const apiParams = {}
      if (isManager && userBranchId) {
        apiParams.branchId = userBranchId
        console.log('🏢 Manager branch filter applied to GRN payments:', {
          userId: currentUser?._id,
          branchId: userBranchId,
          branchName: 'Branch filtering enabled'
        })
      }

      const response = await GRNService.getAllGRNPayments(apiParams)
      
      if (response?.success) {
        // Debug: Log the actual response structure
        console.log('💰 API Response structure:', response.data)
        
        // Extract payments array correctly - the API returns data.payments directly
        const paymentsArray = response.data?.payments || []
        console.log('💰 Raw payments from API:', paymentsArray.length)
        
        // ALWAYS apply frontend filtering for managers (even if API should filter)
        // This ensures security even if backend filtering fails
        let filteredPayments = paymentsArray
        if (isManager && userBranchId) {
          console.log('🔍 MANAGER FILTERING - Starting with', paymentsArray.length, 'payments')
          console.log('🔍 Manager Branch ID:', userBranchId)
          
          // Try multiple branch ID patterns to be safe
          filteredPayments = paymentsArray.filter(payment => {
            // Try different ways to access branch ID
            const branchId1 = payment.grn?.purchaseOrder?.requestingBranch?._id
            const branchId2 = payment.grn?.purchaseOrder?.requestingBranch
            const branchId3 = payment.grn?.purchaseOrder?.branch?._id
            const branchId4 = payment.grn?.purchaseOrder?.branch
            const branchId5 = payment.grn?.branchId
            const branchId6 = payment.branchId
            
            const possibleBranchIds = [branchId1, branchId2, branchId3, branchId4, branchId5, branchId6].filter(Boolean)
            const matches = possibleBranchIds.includes(userBranchId)
            
            if (payment._id) {
              console.log(`Payment ${payment._id.slice(-8)}:`, {
                possibleBranchIds,
                userBranchId,
                matches,
                grnExists: !!payment.grn,
                poExists: !!payment.grn?.purchaseOrder
              })
            }
            
            return matches
          })
          
          console.log('🏢 MANAGER FILTERING RESULT:', {
            originalCount: paymentsArray.length,
            filteredCount: filteredPayments.length,
            userBranchId: userBranchId
          })
          
          // If no payments match, show a warning but don't break the app
          if (paymentsArray.length > 0 && filteredPayments.length === 0) {
            console.warn('⚠️ NO PAYMENTS MATCH MANAGER BRANCH FILTER')
            console.warn('This might indicate a data structure issue or API problem')
            console.warn('Sample payment structure:', paymentsArray[0])
          }
        }
        
        // Log sample payment structure if available
        if (filteredPayments.length > 0) {
          console.log('💰 Sample payment structure:', filteredPayments[0])
        }

        // Process and format the payments data
        const processedPaymentsData = {
          payments: filteredPayments,
          summary: {
            totalPayments: filteredPayments.length,
            totalAmount: filteredPayments.reduce((sum, payment) => sum + (payment.amountPaid || 0), 0),
            totalGRNs: new Set(filteredPayments.map(payment => payment.grn?._id).filter(Boolean)).size,
            paymentMethods: [...new Set(filteredPayments.map(payment => payment.paymentType).filter(Boolean))]
          }
        }

        console.log('💰 Processed payments data:', processedPaymentsData)
        setPaymentsData(processedPaymentsData)
        
      } else {
        console.warn('💰 API request unsuccessful:', response)
        setError('Failed to fetch GRN payments')
        setPaymentsData({ payments: [], summary: {} })
      }
      
    } catch (error) {
      console.error('❌ Error fetching GRN payments data:', error)
      setError(error.message || 'Failed to fetch GRN payments')
      setPaymentsData({ payments: [], summary: {} })
    } finally {
      setLoading(false)
    }
  }

  // Fetch branches for dropdown
  const fetchBranches = async () => {
    try {
      const response = await BranchService.getAllBranches()
      if (response?.success) {
        const branchesData = response.data?.branches || []
        setBranches(branchesData.filter(branch => branch.isActive))
      }
    } catch (error) {
      console.error('❌ Error fetching branches:', error)
    }
  }

  // Fetch suppliers for dropdown
  const fetchSuppliers = async () => {
    try {
      const response = await SuppliersService.getAllSuppliers()
      if (response?.success) {
        const suppliersData = response.data?.suppliers || []
        setSuppliers(suppliersData.filter(supplier => supplier.isActive))
      }
    } catch (error) {
      console.error('❌ Error fetching suppliers:', error)
    }
  }

  // Filter payments based on search and filter criteria
  const filterPayments = useCallback(() => {
    console.log('🔍 Filtering payments. PaymentsData:', paymentsData)
    
    if (!paymentsData?.payments || !Array.isArray(paymentsData.payments)) {
      console.warn('🔍 No valid payments data available for filtering')
      setFilteredPayments([])
      return
    }

    let filtered = [...paymentsData.payments]
    console.log('🔍 Starting with payments:', filtered.length)

    // RBAC: For managers, enforce branch filtering first (security layer)
    if (isManager && userBranchId) {
      const beforeCount = filtered.length
      
      filtered = filtered.filter(payment => {
        // Try multiple branch ID patterns
        const branchId1 = payment.grn?.purchaseOrder?.requestingBranch?._id
        const branchId2 = payment.grn?.purchaseOrder?.requestingBranch
        const branchId3 = payment.grn?.purchaseOrder?.branch?._id
        const branchId4 = payment.grn?.purchaseOrder?.branch
        const branchId5 = payment.grn?.branchId
        const branchId6 = payment.branchId
        
        const possibleBranchIds = [branchId1, branchId2, branchId3, branchId4, branchId5, branchId6].filter(Boolean)
        return possibleBranchIds.includes(userBranchId)
      })
      
      console.log(`🏢 Manager branch filter applied: ${beforeCount} → ${filtered.length} payments for branch ${userBranchId}`)
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(payment => {
        const grnNumber = payment.grn?.grnNumber?.toLowerCase() || ''
        const supplierName = payment.grn?.purchaseOrder?.supplier?.name?.toLowerCase() || ''
        const branchName = payment.grn?.purchaseOrder?.requestingBranch?.name?.toLowerCase() || ''
        const paymentType = payment.paymentType?.toLowerCase() || ''
        const note = payment.note?.toLowerCase() || ''
        
        return grnNumber.includes(searchLower) ||
               supplierName.includes(searchLower) ||
               branchName.includes(searchLower) ||
               paymentType.includes(searchLower) ||
               note.includes(searchLower)
      })
      console.log(`📝 Search filter applied: ${filtered.length} payments match "${searchTerm}"`)
    }

    // Apply branch filter (for admins only, managers already filtered above)
    if (branchFilter && branchFilter !== '' && !isManager) {
      filtered = filtered.filter(payment => 
        payment.grn?.purchaseOrder?.requestingBranch?._id === branchFilter ||
        payment.grn?.purchaseOrder?.requestingBranch?.name === branchFilter
      )
      console.log(`🏢 Branch filter applied: ${filtered.length} payments for branch "${branchFilter}"`)
    }

    // Apply supplier filter
    if (supplierFilter && supplierFilter !== '') {
      filtered = filtered.filter(payment => 
        payment.grn?.purchaseOrder?.supplier?._id === supplierFilter ||
        payment.grn?.purchaseOrder?.supplier?.name === supplierFilter
      )
      console.log(`🏭 Supplier filter applied: ${filtered.length} payments for supplier "${supplierFilter}"`)
    }

    // Apply payment method filter
    if (paymentMethodFilter && paymentMethodFilter !== '') {
      filtered = filtered.filter(payment => payment.paymentType === paymentMethodFilter)
      console.log(`💳 Payment method filter applied: ${filtered.length} payments with method "${paymentMethodFilter}"`)
    }

    // Apply payment date filter
    if (paymentDateFilter && paymentDateFilter !== '') {
      const filterDate = new Date(paymentDateFilter).toDateString()
      filtered = filtered.filter(payment => {
        const paymentDate = new Date(payment.paymentDate).toDateString()
        return paymentDate === filterDate
      })
      console.log(`📅 Payment date filter applied: ${filtered.length} payments on "${paymentDateFilter}"`)
    }

    // Apply date range filter
    if (dateRangeFilter.start || dateRangeFilter.end) {
      filtered = filtered.filter(payment => {
        const paymentDate = new Date(payment.paymentDate)
        let matchesRange = true
        
        if (dateRangeFilter.start) {
          const startDate = new Date(dateRangeFilter.start)
          matchesRange = matchesRange && paymentDate >= startDate
        }
        
        if (dateRangeFilter.end) {
          const endDate = new Date(dateRangeFilter.end)
          endDate.setHours(23, 59, 59, 999) // Include the entire end date
          matchesRange = matchesRange && paymentDate <= endDate
        }
        
        return matchesRange
      })
      console.log(`📊 Date range filter applied: ${filtered.length} payments in range`)
    }

    // FINAL SECURITY CHECK: Ensure managers can NEVER see other branch data
    if (isManager && userBranchId) {
      const beforeSecurityFilter = filtered.length
      filtered = filtered.filter(payment => {
        // Try multiple branch ID patterns - be extremely thorough
        const branchChecks = [
          payment.grn?.purchaseOrder?.requestingBranch?._id === userBranchId,
          payment.grn?.purchaseOrder?.requestingBranch === userBranchId,
          payment.grn?.purchaseOrder?.branch?._id === userBranchId,
          payment.grn?.purchaseOrder?.branch === userBranchId,
          payment.grn?.branchId === userBranchId,
          payment.branchId === userBranchId
        ]
        
        const hasMatch = branchChecks.some(check => check === true)
        
        if (!hasMatch) {
          console.log('🚨 SECURITY: Blocked payment from other branch:', {
            paymentId: payment._id?.slice(-8),
            paymentBranch: payment.grn?.purchaseOrder?.requestingBranch?.name || payment.grn?.purchaseOrder?.requestingBranch,
            managerBranch: userBranchId
          })
        }
        
        return hasMatch
      })
      
      console.log(`🛡️ FINAL SECURITY FILTER: ${beforeSecurityFilter} → ${filtered.length} payments (${beforeSecurityFilter - filtered.length} blocked)`)
    }

    console.log('✅ Final filtered payments:', filtered.length, 'items')
    setFilteredPayments(filtered)
  }, [paymentsData, searchTerm, branchFilter, supplierFilter, paymentDateFilter, paymentMethodFilter, dateRangeFilter, isManager, userBranchId])

  // Handle payment deletion
  const handleDeletePayment = async () => {
    if (!selectedPayment?._id) return

    try {
      const response = await GRNService.deleteGRNPayment(selectedPayment._id)
      if (response?.success) {
        showToast('Payment deleted successfully', 'success')
        fetchPaymentsData() // Refresh data
      }
    } catch (error) {
      console.error('❌ Error deleting payment:', error)
      showToast(error.message || 'Failed to delete payment', 'error')
    } finally {
      setShowDeleteModal(false)
      setSelectedPayment(null)
    }
  }

  // Handle payment success
  const handlePaymentSuccess = () => {
    showToast('Payment recorded successfully', 'success')
    setShowPayModal(false)
    setSelectedGRN(null)
    fetchPaymentsData() // Refresh data
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setBranchFilter('')
    setSupplierFilter('')
    setPaymentDateFilter('')
    setPaymentMethodFilter('')
    setDateRangeFilter({ start: '', end: '' })
  }

  // Export to CSV function
  const exportToCSV = () => {
    try {
      // Prepare CSV headers
      const headers = [
        'Payment Date',
        'GRN Number',
        'Branch',
        'Supplier',
        'Payment Method',
        'Amount Paid (LKR)',
        'Note',
        'Created Date'
      ]

      // Prepare CSV data
      const csvData = filteredPayments.map(payment => [
        formatDate(payment.paymentDate),
        payment.grn?.grnNumber || 'N/A',
        payment.grn?.purchaseOrder?.requestingBranch?.name || 'N/A',
        payment.grn?.purchaseOrder?.supplier?.name || 'N/A',
        getPaymentMethodConfig(payment.paymentType).label,
        payment.amountPaid || 0,
        payment.note || 'No note',
        formatDate(payment.createdAt)
      ])

      // Combine headers and data
      const csvContent = [headers, ...csvData]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n')

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      
      link.setAttribute('href', url)
      link.setAttribute('download', `grn-payments-${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      showToast(`Successfully exported ${filteredPayments.length} payments to CSV`, 'success')
    } catch (error) {
      console.error('❌ Error exporting to CSV:', error)
      showToast('Failed to export payments to CSV', 'error')
    }
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  // Get payment method configuration
  const getPaymentMethodConfig = (method) => {
    const configs = {
      [PAYMENT_METHODS.CASH]: { 
        icon: DollarSign, 
        label: 'Cash', 
        color: 'text-green-600',
        bgColor: 'bg-green-100'
      },
      [PAYMENT_METHODS.CHEQUE]: { 
        icon: FileText, 
        label: 'Cheque', 
        color: 'text-blue-600',
        bgColor: 'bg-blue-100'
      },
      [PAYMENT_METHODS.BANK_TRANSFER]: { 
        icon: CreditCard, 
        label: 'Bank Transfer', 
        color: 'text-purple-600',
        bgColor: 'bg-purple-100'
      },
      [PAYMENT_METHODS.CARD]: { 
        icon: CreditCard, 
        label: 'Card', 
        color: 'text-orange-600',
        bgColor: 'bg-orange-100'
      }
    }
    return configs[method] || { 
      icon: DollarSign, 
      label: method, 
      color: 'text-gray-600',
      bgColor: 'bg-gray-100'
    }
  }

  // Pagination calculations
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentPayments = filteredPayments.slice(startIndex, endIndex)

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchPaymentsData(),
        fetchBranches(),
        fetchSuppliers()
      ])
    }
    loadData()
  }, [])

  // Filter payments when data or filters change
  useEffect(() => {
    filterPayments()
  }, [filterPayments])

  if (loading) {
    return (
      <InlineLoader text="Loading GRN payments..." />
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Payments</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={fetchPaymentsData} className="flex items-center space-x-2">
            <RefreshCw className="h-4 w-4" />
            <span>Retry</span>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Enhanced Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">GRN Payments</h1>

        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="flex items-center space-x-2 border-gray-300 hover:bg-gray-50"
            disabled={dataLoading}
          >
            <RefreshCw className={`h-4 w-4 ${dataLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          
          {/* Debug Button for Managers */}
          {isManager && (
            <Button
              onClick={() => {
                console.log('🔍 DEBUG GRN PAYMENTS:')
                console.log('User:', { role: currentUser?.role, branchId: userBranchId, isManager })
                console.log('Payments Data:', paymentsData)
                console.log('Filtered Payments:', filteredPayments)
                console.log('Branch Filter State:', branchFilter)
                if (paymentsData?.payments?.length > 0) {
                  console.log('Sample Payment:', paymentsData.payments[0])
                }
              }}
              variant="outline"
              className="flex items-center space-x-2 border-red-300 hover:bg-red-50 text-red-600"
            >
              🔍 Debug
            </Button>
          )}
          
          <Button
            onClick={exportToCSV}
            variant="outline"
            className="flex items-center space-x-2 border-gray-300 hover:bg-gray-50"
            disabled={filteredPayments.length === 0}
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export</span>
          </Button>
        </div>
      </div>

      {/* Enhanced Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          icon={DollarSign}
          title="Total Payments"
          value={paymentsData.summary.totalPayments?.toLocaleString() || '0'}
          color="blue"
        />
        <StatsCard
          icon={CreditCard}
          title="Total Amount"
          value={formatCurrency(paymentsData.summary.totalAmount || 0)}
          color="green"
        />
        <StatsCard
          icon={FileText}
          title="Paid GRNs"
          value={paymentsData.summary.totalGRNs?.toLocaleString() || '0'}
          color="purple"
        />
        <StatsCard
          icon={Truck}
          title="Payment Methods"
          value={paymentsData.summary.paymentMethods?.length?.toLocaleString() || '0'}
          color="orange"
        />
      </div>

      {/* Enhanced Filters Card */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Payments</h2>
            </div>
            {(searchTerm || branchFilter || supplierFilter || paymentDateFilter || paymentMethodFilter || dateRangeFilter.start || dateRangeFilter.end) && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
                size="sm"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search by GRN No, Supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              />
            </div>

            {/* Branch Filter - disabled for managers who can only see their own branch */}
            <div className="relative">
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                disabled={isManager}
                className={`w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none appearance-none bg-white ${
                  isManager 
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                    : 'focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500'
                }`}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="">
                  {isManager ? `${currentUser?.branch?.name || 'Your Branch'} (Current)` : 'All Branches'}
                </option>
                {!isManager && branches.map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              {isManager && (
                <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
                  <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                    Branch Restricted
                  </span>
                </div>
              )}
            </div>

            {/* Supplier Filter */}
            <div className="relative">
              <select
                value={supplierFilter}
                onChange={(e) => setSupplierFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="">All Suppliers</option>
                {suppliers.map(supplier => (
                  <option key={supplier._id} value={supplier._id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Method Filter */}
            <div className="relative">
              <select
                value={paymentMethodFilter}
                onChange={(e) => setPaymentMethodFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="">All Methods</option>
                {Object.values(PAYMENT_METHODS).map(method => {
                  const config = getPaymentMethodConfig(method)
                  return (
                    <option key={method} value={method}>
                      {config.label}
                    </option>
                  )
                })}
              </select>
            </div>

            {/* Date Filter */}
            <div className="relative">
              <Input
                type="date"
                placeholder="Payment Date"
                value={paymentDateFilter}
                onChange={(e) => setPaymentDateFilter(e.target.value)}
                className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              />
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <Input
                type="date"
                value={dateRangeFilter.start}
                onChange={(e) => setDateRangeFilter(prev => ({ ...prev, start: e.target.value }))}
                className="h-11 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <Input
                type="date"
                value={dateRangeFilter.end}
                onChange={(e) => setDateRangeFilter(prev => ({ ...prev, end: e.target.value }))}
                className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              />
            </div>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
            Showing <span className="font-medium text-gray-900">{filteredPayments.length}</span> of <span className="font-medium text-gray-900">{paymentsData.payments?.length || 0}</span> payments
            {dataLoading && (
              <span className="ml-2 text-orange-600">
                <ButtonSpinner />
                Loading data...
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Payments List */}
      <Card className="shadow-sm">
        <CardHeader className="border-b border-gray-200">
          <CardTitle className="flex items-center space-x-2">

            <span>Payment History</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredPayments.length > 0 ? (
            <div className="overflow-x-auto">
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <table className="w-full min-w-full bg-white">
                  {/* Table Header */}
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        GRN No
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Branch
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Supplier
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Method
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount Paid
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Note
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  {/* Table Body */}
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentPayments.map((payment) => {
                      const methodConfig = getPaymentMethodConfig(payment.paymentType)
                      const IconComponent = methodConfig.icon
                      
                      return (
                        <tr key={payment._id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">
                                {formatDate(payment.paymentDate)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-blue-100 rounded-lg">
                                <Package className="h-4 w-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{payment.grn?.grnNumber || 'N/A'}</p>
                                <p className="text-sm text-gray-500">ID: {payment.grn?._id?.slice(-8) || 'N/A'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Building className="h-4 w-4 text-gray-400" />
                              <div className="max-w-xs">
                                <p className="font-medium text-gray-900 truncate" title={payment.grn?.purchaseOrder?.requestingBranch?.name || 'N/A'}>
                                  {payment.grn?.purchaseOrder?.requestingBranch?.name || 'N/A'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <div className="max-w-xs">
                                <p className="font-medium text-gray-900 truncate" title={payment.grn?.purchaseOrder?.supplier?.name || 'N/A'}>
                                  {payment.grn?.purchaseOrder?.supplier?.name || 'N/A'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${methodConfig.bgColor} ${methodConfig.color}`}>
                              <IconComponent className="h-3 w-3 mr-1" />
                              {methodConfig.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {/* <DollarSign className="h-4 w-4 text-gray-400" /> */}
                              <span className="text-gray-900 font-medium">
                                {formatCurrency(payment.amountPaid)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="max-w-xs">
                              <p className="text-sm text-gray-600 truncate" title={payment.note || 'No note'}>
                                {payment.note || 'No note'}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                onClick={() => {
                                  setSelectedPayment(payment)
                                  setShowDeleteModal(true)
                                }}
                                className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                                title="Delete Payment"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4 p-4">
                {currentPayments.map((payment) => {
                  const methodConfig = getPaymentMethodConfig(payment.paymentType)
                  const IconComponent = methodConfig.icon

                  return (
                    <div
                      key={payment._id}
                      className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${methodConfig.bgColor}`}>
                            <IconComponent className={`h-5 w-5 ${methodConfig.color}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {payment.grn?.grnNumber || 'N/A'}
                            </h3>
                            <p className="text-sm text-gray-600">{methodConfig.label}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">
                            {formatCurrency(payment.amountPaid)}
                          </p>
                          <p className="text-sm text-gray-600">
                            {formatDate(payment.paymentDate)}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                        <div>
                          <span className="text-gray-600">Branch:</span>
                          <p className="font-medium text-gray-900 truncate">
                            {payment.grn?.purchaseOrder?.requestingBranch?.name || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-600">Supplier:</span>
                          <p className="font-medium text-gray-900 truncate">
                            {payment.grn?.purchaseOrder?.supplier?.name || 'N/A'}
                          </p>
                        </div>
                      </div>

                      {payment.note && (
                        <div className="mb-3 p-2 bg-gray-50 rounded text-sm">
                          <span className="text-gray-600">Note:</span>
                          <p className="text-gray-800">{payment.note}</p>
                        </div>
                      )}

                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedPayment(payment)
                            setShowDeleteModal(true)
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t bg-gray-50 px-6 py-4">
                  <div className="text-sm text-gray-700">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredPayments.length)} of{' '}
                    {filteredPayments.length} payments
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 px-6">
              <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {paymentsData.payments?.length === 0 ? 'No payments found' : 'No payments match your filters'}
              </h3>
              <p className="text-gray-600 mb-4">
                {paymentsData.payments?.length === 0 
                  ? 'No GRN payments have been recorded yet.' 
                  : 'Try adjusting your search criteria.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pay GRN Modal */}
      {showPayModal && selectedGRN && (
        <PayGRNModal
          grn={selectedGRN}
          onClose={() => {
            setShowPayModal(false)
            setSelectedGRN(null)
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedPayment && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedPayment(null)
          }}
          onConfirm={handleDeletePayment}
          title="Delete Payment"
          message={`Are you sure you want to delete this payment of ${formatCurrency(selectedPayment.amountPaid)} for GRN ${selectedPayment.grn?.grnNumber}?`}
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
    </div>
  )
}

export default GRNPayments